import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Mail,
  Phone,
  MapPin,
  ToggleLeft,
  ToggleRight,
  Eye,
  Settings,
  ImageIcon,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";

const CompaniesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deleteCompany, setDeleteCompany] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    legal_name: "",
    rnc: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    currency: "RD$",
    tax_rate: 18.0,
    monthly_goal: 500000,
  });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("No tienes permisos para ver las empresas");
      } else {
        toast.error("Error al cargar empresas");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Empresa actualizada exitosamente");
      } else {
        await axios.post(`${API}/companies`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Empresa creada exitosamente");
      }
      setShowModal(false);
      setEditingCompany(null);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar empresa");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      legal_name: "",
      rnc: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      currency: "RD$",
      tax_rate: 18.0,
      monthly_goal: 500000,
    });
    setLogoPreview(null);

  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      code: company.code,
      name: company.name,
      legal_name: company.legal_name || "",
      rnc: company.rnc || "",
      email: company.email || "",
      phone: company.phone || "",
      address: company.address || "",
      city: company.city || "",
      currency: company.currency || "RD$",
      tax_rate: company.tax_rate || 18.0,
      monthly_goal: company.monthly_goal || 500000,
      logo_url: company.logo_url || "",

    });
    setLogoPreview(company.logo_url || null);

    setShowModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Por favor selecciona una imagen válida");
        return;
      }
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("La imagen no debe superar 2MB");
        return;
      }
      // Convert to base64 for preview and storage
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setLogoPreview(base64);
        setFormData(prev => ({ ...prev, logo_url: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_url: "" }));
  };

  const handleDelete = async () => {
    if (!deleteCompany) return;
    try {
      await axios.delete(`${API}/companies/${deleteCompany.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Empresa eliminada exitosamente");
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar empresa");
    } finally {
      setDeleteCompany(null);
    }
  };

  const handleToggleStatus = async (company) => {
    try {
      const response = await axios.patch(
        `${API}/companies/${company.id}/status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Empresa ${response.data.status === "active" ? "activada" : "desactivada"}`);
      fetchCompanies();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Gestión de Empresas</h2>
          <p className="text-sm text-slate-500">{companies.length} empresas registradas</p>
        </div>
        <Button
          onClick={() => {
            setEditingCompany(null);
            resetForm();
            setShowModal(true);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus size={18} className="mr-2" />
          Nueva Empresa
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          placeholder="Buscar por nombre o código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11"
        />
      </div>

      {/* Companies Grid */}
      {filteredCompanies.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
          <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-800 mb-2">No hay empresas</h3>
          <p className="text-slate-500">Comienza creando tu primera empresa</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCompanies.map((company) => (
            <div
              key={company.id}
              className={`bg-white rounded-lg border ${company.status === "active" ? "border-slate-200" : "border-red-200 bg-red-50"
                } p-5 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${company.status === "active" ? "bg-purple-100" : "bg-red-100"
                    }`}>
                    <Building2 size={24} className={company.status === "active" ? "text-purple-600" : "text-red-600"} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{company.name}</h3>
                    <span className="text-xs text-slate-500 mono">{company.code}</span>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${company.status === "active"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-red-100 text-red-700"
                  }`}>
                  {company.status === "active" ? "Activa" : "Inactiva"}
                </span>
              </div>

              <div className="space-y-2 text-sm mb-4">
                {company.email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail size={14} className="text-slate-400" />
                    <span className="truncate">{company.email}</span>
                  </div>
                )}
                {company.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone size={14} className="text-slate-400" />
                    <span>{company.phone}</span>
                  </div>
                )}
                {company.city && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin size={14} className="text-slate-400" />
                    <span>{company.city}</span>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-lg font-semibold text-slate-800">{company.total_users || 0}</p>
                  <p className="text-xs text-slate-500">Usuarios</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-slate-800">{company.total_clients || 0}</p>
                  <p className="text-xs text-slate-500">Clientes</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/superadmin/companies/${company.id}/users`)}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                >
                  <Users size={16} className="mr-1" />
                  Usuarios
                </Button>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToggleStatus(company)}
                    className="h-8 w-8"
                    title={company.status === "active" ? "Desactivar" : "Activar"}
                  >
                    {company.status === "active" ? (
                      <ToggleRight size={18} className="text-emerald-600" />
                    ) : (
                      <ToggleLeft size={18} className="text-slate-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(company)}
                    className="h-8 w-8 text-slate-600"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteCompany(company)}
                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Company Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={20} className="text-purple-600" />
              {editingCompany ? "Editar Empresa" : "Nueva Empresa"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Show Company ID when editing (read-only) */}
            {editingCompany && (
              <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-slate-500">ID de Empresa</Label>
                <p className="font-mono text-sm text-slate-700 bg-white px-3 py-2 rounded border">
                  {editingCompany.id}
                </p>
              </div>
            )}
            {/* Logo Section */}
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Label className="flex items-center gap-2">
                <ImageIcon size={16} className="text-purple-600" />
                Logo de la Empresa
              </Label>
              <div className="flex items-center gap-4">
                {/* Logo Preview */}
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-white">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 size={32} className="text-slate-300" />
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors">
                        <Upload size={16} />
                        Subir Logo
                      </span>
                    </label>
                    {logoPreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeLogo}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X size={16} className="mr-1" />
                        Quitar
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    PNG, JPG o GIF. Máximo 2MB.
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-code">Código *</Label>
                <Input
                  id="company-code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="EMPRESA1"
                  maxLength={20}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-rnc">RNC</Label>
                <Input
                  id="company-rnc"
                  value={formData.rnc}
                  onChange={(e) => setFormData({ ...formData, rnc: e.target.value })}
                  placeholder="101-23456-7"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-name">Nombre Comercial *</Label>
              <Input
                id="company-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Mi Empresa"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-legal-name">Razón Social</Label>
              <Input
                id="company-legal-name"
                value={formData.legal_name}
                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                placeholder="Mi Empresa, SRL"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-email">Email</Label>
                <Input
                  id="company-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="info@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-phone">Teléfono</Label>
                <Input
                  id="company-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="809-555-1234"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-city">Ciudad</Label>
                <Input
                  id="company-city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Santo Domingo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-currency">Moneda</Label>
                <select
                  id="company-currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                >
                  <option value="RD$">RD$ (Peso Dominicano)</option>
                  <option value="USD">USD (Dólar)</option>
                  <option value="EUR">EUR (Euro)</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-address">Dirección</Label>
              <Input
                id="company-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Av. Principal #123"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-tax">Tasa ITBIS (%)</Label>
                <Input
                  id="company-tax"
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-goal">Meta Mensual</Label>
                <Input
                  id="company-goal"
                  type="number"
                  value={formData.monthly_goal}
                  onChange={(e) => setFormData({ ...formData, monthly_goal: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving ? "Guardando..." : editingCompany ? "Actualizar" : "Crear Empresa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCompany} onOpenChange={() => setDeleteCompany(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará <strong>{deleteCompany?.name}</strong> y todos sus datos relacionados
              (usuarios, clientes, artículos, documentos, etc.). Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar Todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompaniesPage;
